/**
 * Coordination between the legacy native token handoff and the SSO bridge.
 *
 * Inside the native WebView the app can be signed in two ways, and they must
 * not both run: `components/Root` asks the Flutter bridge for a refresh token
 * (`getRefreshToken`) and mints a session from it, while `sagas/auth/bridgeSsoSaga`
 * may navigate the page to the IDP for a silent login. Whoever wins the race
 * aborts the other, so the bridge waits here until the handoff has answered.
 *
 * The query-string handoff (`?UserToken=` / `?refreshToken=`) needs no
 * coordination — `auth/ssoBridge` sees it in the URL and stands down directly.
 */

/** How long to wait before assuming the native handoff will never answer. */
export const HANDOFF_TIMEOUT_MS = 10000;

let settle;
const settled = new Promise(resolve => {
    settle = resolve;
});

/** Whether the page is running inside the native app's WebView. */
export function hasNativeHandoff() {
    return typeof window !== 'undefined' && Boolean(window.flutter_inappwebview);
}

/**
 * Report that the native token handoff has finished. Must be called on every
 * path, including the one where the native side handed over nothing — the
 * bridge is blocked until it is.
 *
 * @param {boolean} hydrated Whether a session was created from a native token.
 * @returns {void}
 */
export function markNativeHandoffSettled(hydrated) {
    settle(Boolean(hydrated));
}

/**
 * Wait for the native token handoff.
 *
 * Resolves `false` after {@link HANDOFF_TIMEOUT_MS} rather than hanging: the
 * caller is usually holding a loading screen, and proceeding without a session
 * is recoverable where waiting forever is not.
 *
 * @returns {Promise<boolean>} whether the handoff created a session.
 */
export function whenNativeHandoffSettled() {
    let timer;
    const timeout = new Promise(resolve => {
        timer = setTimeout(() => resolve(false), HANDOFF_TIMEOUT_MS);
    });
    return Promise.race([settled, timeout]).finally(() => clearTimeout(timer));
}
